<template>
    <div class="">
      <!-- Scope filter bar. Mirrors Coding > Operations V2 so the two surfaces
           reconcile row for row — see TaskViewSet.counts for the parity contract.
           Both checkboxes resolve against the owning OPPORTUNITY, not the task,
           which is why they say "opps" — same wording as Ops V2. -->
      <div class="flex items-center flex-wrap gap-3 pb-3 mb-4" style="border-bottom: 1px solid #E8E9E9;">
        <label class="text-xs font-medium whitespace-nowrap" style="color: #626264;">Period</label>
        <div class="flex items-center gap-1.5">
          <button
            v-for="preset in datePresets"
            :key="preset.key"
            class="px-2 py-1 text-xs rounded-md transition-colors"
            :style="datePresetStyle(preset.key)"
            @click="setDatePreset(preset.key)"
          >{{ preset.label }}</button>
        </div>
        <span v-if="dateStart" class="text-xs" style="color: #9A9A9C;">
          {{ dateStart }} → {{ dateEnd }}
        </span>

        <label
          class="flex items-center gap-1.5 text-xs cursor-pointer ml-2"
          style="color: #626264;"
          title="Resolved on the OPPORTUNITY, not the task — an expired opp hides its tasks too. Matches Coding > Operations V2."
        >
          <input v-model="includeExpired" type="checkbox" @change="fetchTaskCounts" />
          Include expired opps
        </label>
        <label
          class="flex items-center gap-1.5 text-xs cursor-pointer"
          style="color: #626264;"
          title="Administrative bulk-close sweeps rather than completed review work. An opp is admin-closed when ANY of its tasks carries an administrative-close action; hiding it hides all of its tasks. Matches Coding > Operations V2."
        >
          <input v-model="includeAdminClosed" type="checkbox" @change="fetchTaskCounts" />
          Include admin-closed opps
        </label>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex items-center justify-center w-full h-32">
        <div class="w-12 h-12">
          <SpinningLoader />
        </div>
      </div>
      <div v-else class="flex items-start gap-2 justify-start">
         <div v-for="(metric_type, metric_key) in return_parsed_data"
                 :key="metric_key"
                 class="bg-white rounded-lg shadow-sm overflow-hidden flex min-w-0"
            >
              <!-- Metric Container -->
              <div class="relative min-w-0">
                <!-- Colored Top Bar -->
                <div class="h-1 w-full"
                     :style="{ backgroundColor: getMetricColor(metric_type) }">
                </div>

                <!-- Content Area -->
                <div class="p-4 min-w-0">
                  <!-- Metric Header -->
                  <div class="mb-3 min-w-0">
                    <h3 class="text-sm font-semibold text-gray-700 uppercase tracking-wider truncate">
                      {{ formatMetricName(metric_key) }}
                    </h3>
                    <div class="text-2xl font-bold mt-1"
                         :style="{ color: getMetricColor(metric_type) }">
                      {{ getMetricTotal(metric_type) }}
                      <span class="text-xs font-normal text-gray-500">complete</span>
                    </div>
                  </div>

                  <!-- Task Types Column -->
                  <div class="flex flex-col gap-2 min-w-0">
                    <div v-for="(task_type, key) in metric_type"
                         :key="key"
                         class="group relative bg-gray-50 rounded-lg p-3 cursor-pointer transition-all duration-200 hover:bg-gray-100 border-2 border-transparent"
                         :style="{
                           'backgroundColor': isTaskTypeSelected(metric_key, key) ? getMetricColor(metric_type) + '10' : '',
                           'borderColor': isTaskTypeSelected(metric_key, key) ? getMetricColor(metric_type) : 'transparent'
                         }"
                         @mouseenter="$event.currentTarget.style.borderColor = getMetricColor(metric_type)"
                         @mouseleave="$event.currentTarget.style.borderColor = isTaskTypeSelected(metric_key, key) ? getMetricColor(metric_type) : 'transparent'"
                         @click="toggleTaskType(metric_key, key)"
                    >
                      <!-- Task name and count on same line -->
                      <div class="grid grid-cols-5 gap-2 items-center mb-2 min-w-0">
                        <div class="col-span-3 text-xs font-medium text-gray-600 truncate min-w-0">
                          {{ key }}
                        </div>
                        <div class="col-span-2 text-xs font-medium text-gray-900 text-right truncate min-w-0">
                          {{ task_type.completed || 0 }}/{{ task_type.total || 0 }}
                        </div>
                      </div>

                      <!-- Progress indicator -->
                      <div class="h-0.5 w-full bg-gray-200 rounded-full overflow-hidden">
                        <div class="h-full transition-all duration-500"
                             :style="{
                               width: calculateProgress(task_type) + '%',
                               backgroundColor: getMetricColor(metric_type)
                             }">
                        </div>
                      </div>

                      <!-- On Hold badge -->
                      <div v-if="task_type.on_hold > 0"
                           class="mt-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium cursor-pointer hover:opacity-80"
                           style="background-color: #FEF3C7; color: #92400E;"
                           @click.stop="filterOnHold(metric_key, key, task_type)">
                        {{ task_type.on_hold }} on hold
                      </div>

                      <!-- Selection indicator -->
                      <div v-if="isTaskTypeSelected(metric_key, key)"
                           class="absolute -top-1 -right-1 w-4 h-4 rounded-full shadow-sm flex items-center justify-center"
                           :style="{ backgroundColor: getMetricColor(metric_type) }">
                        <FontAwesomeIcon :icon="['fas','check']" class="text-white text-xs"/>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
      </div>

    </div>
</template>

<script>
import axios from 'axios'
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import SpinningLoader from '@/components/widgetcomponents/Viz_Elements/Spinning_Loader.vue'

export default {
  components: {
    FontAwesomeIcon,
    SpinningLoader,
  },
  props: {
    entity: {
      type: Object,
      required: false
    },
  },
  emits: ['updateTaskTypes'],
  data() {
    return {
      countsData: null,
      selectedTaskTypes: [], // Track selected task types
      metricColors: {}, // Store generated colors for consistency
      loading:false,
      // Scope defaults: YTD, expired + admin-closed charts EXCLUDED. Matches the
      // Ops V2 Tasks tab defaults so the two agree out of the box.
      datePreset: 'ytd',
      dateStart: null,
      dateEnd: null,
      datePresets: [
        { key: 'mtd', label: 'MTD' },
        { key: 'last_month', label: 'Last Month' },
        { key: 'ytd', label: 'YTD' },
        { key: 'all', label: 'All time' },
      ],
      includeExpired: false,
      includeAdminClosed: false,
    }
  },
  computed: {
    return_parsed_data() {
      if (!this.countsData?.results) {
        return {};
      }
      
      const result = {};
      
      // Group the pre-aggregated data by metric
      this.countsData.results.forEach(item => {
        const metric_slug = item.metric_name_slug;
        const taskTypeName = item.task_type_name;
        
        if (!result[metric_slug]) {
          result[metric_slug] = {};
        }
        
        result[metric_slug][taskTypeName] = {
          task_type_id: item.task_type_id,
          task_color: item.task_color,
          total: item.total_count,
          completed: item.completed_count,
          // Ops V2 Completion % denominator — active + completed + on hold +
          // snoozed. Excludes skipped and expired, which are terminal.
          denominator: item.completion_denominator,
          on_hold: item.on_hold_count || 0,
          metric_id: item.metric_id,
          icon: typeof item.task_icon === 'string' && item.task_icon.includes('[') ?
            JSON.parse(item.task_icon.replace(/'/g, '"')) : item.task_icon
        };
      });

      // Fill in missing task types with zero counts
      if (this.countsData?.allowed_task_types) {
        this.countsData.allowed_task_types.forEach(allowedTask => {
          const metric_slug = allowedTask.metric_name_slug;
          
          // If this metric exists in results but this task type is missing
          if (result[metric_slug] && !result[metric_slug][allowedTask.task_type_name]) {
            result[metric_slug][allowedTask.task_type_name] = {
              task_type_id: allowedTask.task_type_id,
              task_color: allowedTask.task_color,
              total: 0,
              completed: 0,
              denominator: 0,
              on_hold: 0,
              metric_id: allowedTask.metric_id,
              icon: allowedTask.task_icon
            };
          }
        });
      }

      // Sort metrics by metric_id first, then sort task types within each metric by task_type_id
      const sortedResult = {};
      const metricsWithIds = Object.entries(result).map(([metric_slug, taskTypes]) => {
        const firstTaskType = Object.values(taskTypes)[0];
        return [metric_slug, taskTypes, firstTaskType?.metric_id || 0];
      }).sort(([,, aMetricId], [,, bMetricId]) => aMetricId - bMetricId);

      metricsWithIds.forEach(([metric_slug, taskTypes]) => {
        // Filter out task types with 0 total
        const nonEmptyEntries = Object.entries(taskTypes)
          .filter(([, tt]) => tt.total > 0)
          .sort(([,a], [,b]) => a.task_type_id - b.task_type_id);
        if (nonEmptyEntries.length > 0) {
          sortedResult[metric_slug] = Object.fromEntries(nonEmptyEntries);
        }
      });

      return sortedResult;
    }
  },
  methods: {
    async fetchTaskCounts(){
      this.loading = true;

      try {
        // Deliberately NO statusIds. The backend now derives Ops V2's TASK_STATE
        // buckets itself, so the total includes skipped + expired exactly as the
        // Ops V2 Tasks tab does. Sending a status whitelist here would re-narrow
        // the population and break that parity — it is also what used to make
        // Skipped tasks invisible while TaskStatus.completed still counted them.
        const params = {
          entityId: this.entity?.id || null,
          includeExpired: this.includeExpired,
          includeAdminClosed: this.includeAdminClosed,
        };
        if (this.dateStart) params.dateStart = this.dateStart;
        if (this.dateEnd) params.dateEnd = this.dateEnd;

        const response = await axios.get(`api/tasks/counts/`, { params });

        if (response.data?.success && response.data?.results?.length > 0) {
          this.countsData = response.data;
        } else {
          this.countsData = { results: [] };
        }
        
        this.loading = false;
      } catch (error) {
        console.error('Error fetching task counts:', error);
        this.loading = false;
      }
    },

    toggleTaskType(metric_slug, taskTypeName) {
      const taskTypeData = this.return_parsed_data[metric_slug][taskTypeName];
      if (!taskTypeData || !taskTypeData.task_type_id) return;

      const taskTypeId = taskTypeData.task_type_id;

      // Check if the task type is already selected
      const index = this.selectedTaskTypes.indexOf(taskTypeId);

      if (index === -1) {
        // Add to selected types
        this.selectedTaskTypes.push(taskTypeId);
      } else {
        // Remove from selected types
        this.selectedTaskTypes.splice(index, 1);
      }
      // Emit the updated task types
      this.$emit('updateTaskTypes', this.selectedTaskTypes);
    },

    filterOnHold(metric_slug, taskTypeName, taskTypeData) {
      if (!taskTypeData || !taskTypeData.task_type_id) return;
      // Select just this task type and emit on-hold status filter
      this.selectedTaskTypes = [taskTypeData.task_type_id];
      this.$emit('updateTaskTypes', this.selectedTaskTypes);
      this.$emit('updateStatusFilter', ['7']);
    },

    isTaskTypeSelected(metric_slug, taskTypeName) {
      const taskTypeData = this.return_parsed_data[metric_slug][taskTypeName];
      if (!taskTypeData || !taskTypeData.task_type_id) return false;

      return this.selectedTaskTypes.includes(taskTypeData.task_type_id);
    },

    // New helper methods
    getMetricColor(metricType) {
      // Use a predefined color palette for metrics
      const colorPalette = [
        '#3B82F6', // blue
        '#10B981', // emerald
        '#F59E0B', // amber
        '#EF4444', // red
        '#8B5CF6', // violet
        '#EC4899', // pink
        '#14B8A6', // teal
        '#F97316', // orange
      ];

      const metricKeys = Object.keys(this.return_parsed_data);
      const metricKey = metricKeys.find(key => this.return_parsed_data[key] === metricType);
      const index = metricKeys.indexOf(metricKey);

      return colorPalette[index % colorPalette.length];
    },

    formatMetricName(metricKey) {
      // Convert snake_case to Title Case
      return metricKey
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },

    setDatePreset(preset) {
      this.datePreset = preset;
      this._applyDatePreset(preset);
      this.fetchTaskCounts();
    },

    _applyDatePreset(preset) {
      // Local YYYY-MM-DD. Avoid toISOString(), which converts to UTC and rolls
      // the date forward in the evening for negative-UTC timezones (US).
      // Deliberately identical to Coding_Operations_V2._applyDatePreset — if one
      // changes, change both, or the two surfaces stop reconciling.
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth();
      const toISO = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      if (preset === 'mtd') {
        this.dateStart = toISO(new Date(year, month, 1));
        this.dateEnd = toISO(now);
      } else if (preset === 'ytd') {
        this.dateStart = `${year}-01-01`;
        this.dateEnd = toISO(now);
      } else if (preset === 'last_month') {
        this.dateStart = toISO(new Date(year, month - 1, 1));
        this.dateEnd = toISO(new Date(year, month, 0));
      } else {
        // 'all' — no bounds, so the backend counts the full lifetime population.
        this.dateStart = null;
        this.dateEnd = null;
      }
    },

    datePresetStyle(key) {
      const active = this.datePreset === key;
      return active
        ? { backgroundColor: '#FEF3F0', color: '#F76734', border: '1px solid #F76734', fontWeight: 600 }
        : { backgroundColor: '#F9FAFB', color: '#626264', border: '1px solid #E8E9E9' };
    },

    calculateProgress(taskType) {
      const completed = taskType.completed || 0;
      // Measured against the completion-rate denominator, not total: skipped and
      // expired tasks are terminal and will never be worked, so leaving them in
      // would permanently cap the bar below 100%. Matches Ops V2 Completion %.
      const denominator = taskType.denominator || 0;
      return denominator > 0 ? Math.round((completed / denominator) * 100) : 0;
    },

    getMetricTotal(metricType) {
      return Object.values(metricType).reduce((sum, taskType) => {
        return sum + (taskType.completed || 0);
      }, 0);
    },

    applyRouteFilter() {
      const program = this.$route?.query?.program;
      const tier = this.$route?.query?.tier;
      if (!program && !tier) return;

      const programToSlug = {
        'Suspect': 'suspects',
        'Resub': 'resubmissions',
        'Early Start': 'early_start',
      };
      const targetSlug = program ? programToSlug[program] : null;
      const tierLower = tier ? tier.toLowerCase() : null;

      const matching = [];
      for (const [slug, taskTypes] of Object.entries(this.return_parsed_data)) {
        if (targetSlug && slug !== targetSlug) continue;
        for (const [name, tt] of Object.entries(taskTypes)) {
          if (tierLower && !name.toLowerCase().includes(tierLower)) continue;
          if (tt.task_type_id) matching.push(tt.task_type_id);
        }
      }

      if (matching.length) {
        this.selectedTaskTypes = matching;
        this.$emit('updateTaskTypes', matching);
      }
    },
  },
  async mounted() {
    this._applyDatePreset(this.datePreset);
    await this.fetchTaskCounts();
    this.applyRouteFilter();
  },
}
</script>

<style scoped>
/* Smooth scrollbar */
.overflow-x-auto {
  scrollbar-width: thin;
  scrollbar-color: #CBD5E1 #F3F4F6;
}

.overflow-x-auto::-webkit-scrollbar {
  height: 8px;
}

.overflow-x-auto::-webkit-scrollbar-track {
  background: #F3F4F6;
  border-radius: 4px;
}

.overflow-x-auto::-webkit-scrollbar-thumb {
  background-color: #CBD5E1;
  border-radius: 4px;
}

.overflow-x-auto::-webkit-scrollbar-thumb:hover {
  background-color: #94A3B8;
}

/* Smooth transitions */
.group {
  transition: all 0.2s ease;
}

.group:hover {
  transform: translateY(-1px);
}

/* Custom ring color using CSS variable */
.ring-2 {
  box-shadow: 0 0 0 2px var(--ring-color);
}
</style>
